"use client";

import AdDetailModel from "@/models/AdDetailModel";
import { Avatar, Box, Button, styled, Typography } from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import { useState } from "react";
import {
  AD_DETAILS_BORDER_RADIUS,
  BOX_SHADOW_SIZE_LARGE,
} from "@/app/layout/LayoutConstants";
import dynamic from "next/dynamic";
import PersonIcon from "@mui/icons-material/Person";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { AD_DETAILS_DESKTOP_SPACING } from "./AdDetailsPageLayout";
import { RevealAdPhone } from "@/actions/details/RevealAdPhone";
import { useRecaptcha } from "@/app/layout/useRecaptcha";
import VerifiedIcon from "@mui/icons-material/Verified";
import { DETAILS_ADS } from "../ads";
import AdModule from "@/components/shared/AdModule";
import { CreateAnalyticEvent } from "@/actions/details/CreateAnalyticEvent";
import ottBadge from "@/../public/img/racing-victoria/rv-ott-ar.png";
import Image from "next/image";
import Link from "next/link";
import { TrackedLink } from "@/components/shared/TrackedLink";
import { TrackedBox } from "@/components/shared/TrackedBox";
import { StickyBox } from "@/app/layout/StickyBox";

// AJB: source of jank?  Maybe allocate vertical space?
const AdEmailEnquiry = dynamic(() => import("./AdEmailEnquiry"), {
  ssr: false,
});

const EnquiryBox = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "flex",
    flexDirection: "column",
    backgroundColor: "white",
    borderRadius: AD_DETAILS_BORDER_RADIUS,
    boxShadow: BOX_SHADOW_SIZE_LARGE,
    width: "100%",
    overflow: "hidden",
    marginBottom: AD_DETAILS_DESKTOP_SPACING,
    alignSelf: "start",
  }),
);

const ProfileBox = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "grid",
    gridTemplateColumns: "min-content 1fr",
    alignItems: "center",
    gap: 2,
    borderBottom: "1px solid",
    borderColor: "divider",
    backgroundColor: "grey.100",
    [theme.breakpoints.down("sm")]: {
      gap: 1,
    },
  }),
);

const ProfileDetails = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "flex",
    flexDirection: "column",
    gap: 0.5,
  }),
);

const ProfileAvatar = ({ ad }: { ad: AdDetailModel }) => {
  const avatarSize = ad.sellerIsVerified ? 120 : 60;
  return (
    <ProfileLink ad={ad}>
      <Avatar
        src={ad.sellerPictureSrc}
        sx={{
          width: avatarSize,
          height: avatarSize,
          backgroundColor: "white",
        }}
      >
        <PersonIcon
          sx={{
            fontSize: "40px",
            color: "grey.500",
          }}
        />
      </Avatar>
    </ProfileLink>
  );
};

const ProfileName = ({ ad }: { ad: AdDetailModel }) => {
  return (
    <ProfileLink ad={ad}>
      <Typography
        variant="h5"
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {ad.sellerName || "Horsezone Seller"}
        <NavigateNextIcon />
      </Typography>
    </ProfileLink>
  );
};

const ProfileVerifiedBadge = ({ ad }: { ad: AdDetailModel }) => {
  return (
    ad.sellerIsVerified && (
      <Box
        sx={{
          display: "flex",
          gap: 0.5,
          alignItems: "center",
          marginLeft: -3.5,
          zIndex: 1,
        }}
      >
        <VerifiedIcon
          sx={{
            color: "#1d9bf0",
            backgroundColor: "white",
            borderRadius: "50%",
          }}
        />
        <Typography variant="body2">Verified Member</Typography>
      </Box>
    )
  );
};

const ProfileHistory = ({ ad }: { ad: AdDetailModel }) => {
  return (
    <Box
      sx={{
        display: "flex",
        gap: 2,
      }}
    >
      <Typography variant="body2">
        {ad.sellerTotalAds ? (
          <>
            {ad.sellerTotalAds} ad{ad.sellerTotalAds > 1 ? "s" : ""}
          </>
        ) : (
          <>Member</>
        )}
        <> since {ad.sellerMemberSince}</>
      </Typography>
      {!ad.sellerIsVerified && <ProfileWebsite ad={ad} />}
    </Box>
  );
};

const ProfileWebsite = ({ ad }: { ad: AdDetailModel }) => {
  return (
    ad.website && (
      <TrackedLink href={ad.website}>
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            textDecoration: "underline",
            alignItems: "center",
          }}
        >
          <Typography variant="body2" component="span">
            {ad.sellerWebsiteLabel || "Website"}
          </Typography>
          <OpenInNewIcon sx={{ height: "14px" }} />
        </Box>
      </TrackedLink>
    )
  );
};

const AdPhoneEnquiry = ({ ad }: { ad: AdDetailModel }) => {
  const [sellerPhoneIsRevealing, setSellerPhoneIsRevealing] = useState(false);
  const [sellerPhoneRevealed, setSellerPhoneRevealed] = useState<
    { local: string; e164: string } | undefined
  >();
  const { getRecaptchaToken } = useRecaptcha();
  return (
    ad.sellerPhoneMasked && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: 3,
          backgroundColor: "grey.100",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
          }}
        >
          {sellerPhoneRevealed && (
            <Typography>
              Tell <strong>{ad.sellerName}</strong> you saw it on{" "}
              <strong>Horsezone</strong>!
            </Typography>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <PhoneIcon /> Call
            </Typography>

            {sellerPhoneRevealed ? (
              <Typography
                component="a"
                href={`tel:${sellerPhoneRevealed.e164}`}
                sx={{ textDecoration: "underline" }}
                onClick={async () => {
                  await CreateAnalyticEvent({
                    event: {
                      adIds: [ad.id],
                      eventType: "AD#ENGAGEMENT#PHONE#CLICK",
                    },
                  });
                }}
              >
                {sellerPhoneRevealed.local}
              </Typography>
            ) : (
              <>
                <Typography>{ad.sellerPhoneMasked}</Typography>
                <Button
                  sx={{
                    fontWeight: "bold",
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "black",
                  }}
                  onClick={async () => {
                    setSellerPhoneIsRevealing(true);
                    const fullSellerPhone = await RevealAdPhone({
                      adId: ad.id,
                      recaptchaToken: await getRecaptchaToken(),
                    });
                    setSellerPhoneRevealed(fullSellerPhone);

                    await CreateAnalyticEvent({
                      event: {
                        adIds: [ad.id],
                        eventType: "AD#ENGAGEMENT#PHONE#REVEAL",
                      },
                    });
                  }}
                  loading={sellerPhoneIsRevealing}
                >
                  Show
                </Button>
              </>
            )}
          </Box>
        </Box>
      </Box>
    )
  );
};

const ProfileFarRight = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    gap: 1,
  }),
);

const ProfileRight = styled(Box)(({ theme }) =>
  theme.unstable_sx({
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gridRowGap: theme.spacing(0.5),
  }),
);

const ProfileRvOttBadge = ({ ad }: { ad: AdDetailModel }) => {
  return (
    ad.isRvOtt && (
      <TrackedBox>
        <Box
          component={Image}
          src={ottBadge}
          alt="Racing Victoria Off The Track  Acknowledged Retrainer"
          width={100}
          height={38}
          sx={{
            marginTop: 1,
            marginRight: 1,
          }}
        />
      </TrackedBox>
    )
  );
};

const ProfileLink = ({
  ad,
  children,
}: {
  ad: AdDetailModel;
  children: React.ReactNode;
}) => {
  return <Link href={`/member/${ad.sellerId}`}>{children}</Link>;
};

export default function AdAside({
  ad,
  innerRef,
}: {
  ad: AdDetailModel;
  innerRef: React.RefObject<HTMLDivElement | null>;
}) {
  return (
    <StickyBox>
      {!ad.isInactive && (
        <EnquiryBox ref={innerRef}>
          <ProfileBox
            sx={(theme) => ({
              padding: ad.sellerIsVerified ? theme.spacing(0.5, 2) : 2,
              [theme.breakpoints.down("sm")]: {
                padding: theme.spacing(0.5),
              },
            })}
          >
            <ProfileAvatar ad={ad} />
            <ProfileDetails>
              <ProfileName ad={ad} />
              <ProfileRight>
                <ProfileDetails>
                  <ProfileVerifiedBadge ad={ad} />
                  <ProfileHistory ad={ad} />
                </ProfileDetails>
                <ProfileFarRight>
                  <ProfileRvOttBadge ad={ad} />
                </ProfileFarRight>
              </ProfileRight>
              {ad.sellerIsVerified && <ProfileWebsite ad={ad} />}
            </ProfileDetails>
          </ProfileBox>
          <AdEmailEnquiry ad={ad} />
          <AdPhoneEnquiry ad={ad} />
        </EnquiryBox>
      )}

      {!ad.sellerIsHideAds && (
        <AdModule config={DETAILS_ADS.Horsezone_Details_Side} />
      )}
    </StickyBox>
  );
}
